import fs from 'node:fs';
import path from 'node:path';
import pc from 'picocolors';
import { ApiClient } from '../client.js';
import { DEFAULT_PRODUCT_NAME } from '../constants.js';
import { readAppConfig } from '../lib/appConfig.js';
import { runChild, npxCommand, pythonCommand, type ExecResult } from '../lib/exec.js';
import { isValidMigrationName, migrationNameError } from '../lib/names.js';
import { resolveProfile } from '../profiles.js';
import { emitJson, fail, human, note, ok, type Io } from '../output.js';

interface DevCreds {
  databaseUrl: string;
  role: string;
  schema: string;
}

export type Backend = 'node' | 'python';

export interface MigrateStep {
  cmd: string;
  args: string[];
  needsShell: boolean;
  /** Human label for progress/error context. */
  label: string;
}

/**
 * The exact child-process invocations a migration runs, in order. Pure +
 * testable — the orchestration in `runMigrate` just executes these with
 * DATABASE_URL set.
 *
 * Node (Prisma): `prisma migrate dev --name <name>` then `prisma generate`.
 *   The dev role has createdb (per db.schema ownerRole), so Prisma can make
 *   its own shadow database — no --create-only, no shadow-URL plumbing.
 * Python: `python -m src.migrate <name>` — the template's migrate entrypoint
 *   (it owns the mechanism: SQLAlchemy create_all today, Alembic later).
 */
export function buildMigrateSteps(backend: Backend, name: string): MigrateStep[] {
  if (backend === 'python') {
    const py = pythonCommand();
    return [{ cmd: py, args: ['-m', 'src.migrate', name], needsShell: false, label: 'apply migration' }];
  }
  const { cmd, needsShell } = npxCommand();
  return [
    { cmd, args: ['prisma', 'migrate', 'dev', '--name', name], needsShell, label: 'apply migration' },
    { cmd, args: ['prisma', 'generate'], needsShell, label: 'regenerate the database client' },
  ];
}

/**
 * Translate a failed prisma/python step into plain language for a
 * non-technical builder, keeping the raw tool output for the curious.
 */
function explainFailure(step: MigrateStep, result: ExecResult): string {
  if (result.spawnError) {
    const missing = step.cmd === 'npx' ? 'Node tooling (npx/prisma)' : 'Python';
    return (
      `Could not start ${missing} to ${step.label} — is it installed and on your PATH? ` +
      `(${result.spawnError})`
    );
  }
  const raw = `${result.stdout}\n${result.stderr}`;
  const lower = raw.toLowerCase();
  let hint = `Failed to ${step.label}.`;
  if (/can't reach database server|connection refused|could not connect|p1001/.test(lower)) {
    hint = `Failed to ${step.label}: couldn't reach the database. Re-run, or check that your dev credentials are current.`;
  } else if (/permission denied|must be owner|p3014|shadow database/.test(lower)) {
    hint =
      `Failed to ${step.label}: your Ghosty database rejected the change — this is ` +
      'usually a setup issue. Contact your Ghosty admin.';
  } else if (/schema|migration|p\d{4}/.test(lower)) {
    hint = `Failed to ${step.label}: the database rejected the schema change.`;
  }
  const tail = raw.trim().split(/\r?\n/).slice(-6).join('\n');
  return tail ? `${hint}\n${tail}` : hint;
}

/**
 * `ghosty migrate <name>` — apply a schema change to the builder's personal
 * dev schema, the same DB `ghosty dev` connects to. Node apps run Prisma;
 * Python apps run the template's migrate entrypoint. Never touches prod.
 */
export async function runMigrate(
  name: string,
  options: { dir?: string; json?: boolean; adminUrl?: string },
): Promise<void> {
  const io: Io = { json: options.json === true, productName: DEFAULT_PRODUCT_NAME };
  const dir = path.resolve(process.cwd(), options.dir ?? '.');

  if (!name || !isValidMigrationName(name)) {
    fail(io, migrationNameError(String(name)), 1, 'INVALID_MIGRATION_NAME');
  }

  const resolved = resolveProfile(options.adminUrl, undefined, dir);
  if (!resolved) fail(io, 'Not signed in. Run `ghosty login` first.', 1, 'NOT_LOGGED_IN');
  io.productName = resolved.profile.branding?.productName ?? DEFAULT_PRODUCT_NAME;

  let config;
  try {
    config = readAppConfig(dir);
  } catch (err) {
    fail(io, (err as Error).message, 1, 'BAD_APP_TREE');
  }

  // No database yet → not a hard failure; the database-model agent enables
  // one on demand. In --json, surface a structured reason rather than exit 1.
  if (config.database !== true) {
    const message =
      `${config.slug} has no database yet, so there's nothing to migrate. ` +
      'A database is enabled on demand by building a data model — ask Claude to create one, ' +
      'or set "database": true in app.config.json and run `ghosty provision`.';
    if (io.json) {
      emitJson({ ok: false, reason: 'no-database', name, slug: config.slug });
      return;
    }
    note(io, message);
    human(io, `${pc.yellow('skipped')} — no database`);
    return;
  }

  const backendDir = path.join(dir, 'backend');
  if (!fs.existsSync(backendDir)) {
    fail(io, `Expected a backend/ folder in ${dir}.`, 1, 'MISSING_FOLDERS');
  }

  // Detect the backend by what the template actually wrote, not just the
  // config flag (a python app has no prisma schema and vice versa).
  const hasPrisma = fs.existsSync(path.join(backendDir, 'prisma', 'schema.prisma'));
  const hasPyModels = fs.existsSync(path.join(backendDir, 'src', 'models'));
  let backend: Backend;
  if (hasPrisma) backend = 'node';
  else if (hasPyModels) backend = 'python';
  else {
    backend = config.backend === 'python' ? 'python' : 'node';
  }

  // Fetch the per-user dev DB creds the SAME way `ghosty dev` does.
  note(io, 'Connecting to your Ghosty database…');
  let creds: DevCreds;
  try {
    const client = new ApiClient(resolved.adminUrl, resolved.profile.token);
    creds = await client.get<DevCreds>(
      `/builder-api/me/dev-creds?slug=${encodeURIComponent(config.slug)}`,
    );
    note(io, '  Connected to your Ghosty database');
  } catch (err) {
    fail(io, `Couldn't connect to your Ghosty database: ${(err as Error).message}`, 1, 'DEV_CREDS_FAILED');
  }

  const steps = buildMigrateSteps(backend, name);
  note(io, `Applying migration "${name}" to your dev database...`);
  for (const step of steps) {
    const result = runChild(step.cmd, step.args, {
      cwd: backendDir,
      env: { DATABASE_URL: creds.databaseUrl },
      needsShell: step.needsShell,
    });
    if (!io.json && result.stdout.trim()) process.stderr.write(`${pc.dim(result.stdout.trim())}\n`);
    if (result.status !== 0) {
      fail(io, explainFailure(step, result), 1, 'MIGRATE_FAILED');
    }
  }

  if (io.json) {
    emitJson({ ok: true, name, slug: config.slug, backend, applied: true });
    return;
  }
  human(io);
  ok(io, `Migration "${name}" applied to your dev database.`);
  note(io, '  Production picks it up on your next deploy.');
  human(io, `${pc.green('ready')} — ${name} applied`);
}
